/**
 * @file BuilderPanel.tsx
 * @description Правая панель конструктора — «что выбрано» (phase19.16
 * Р1-6а; uiux-builder-shell.md §2.1 таблица, §3.2): заголовок с [×], строка
 * «Где действует» (только эта страница / весь сайт — N страниц) и панель:
 * раздел «Весь сайт», блок, шапка или подвал, настройки страницы, пояснение
 * экрана магазина. Шапка и подвал без права настроек — объяснение.
 *
 * Р2а: экран «Каталог» — панель «Каталог» (вид списка и карточки); экран
 * «Страница товара» — блоки шаблона, как на странице, а без выбранного
 * блока — пояснение шаблона (настроек страницы у шаблона нет).
 *
 * SB120 этап 4: экран «Корзина» — панель «Вид корзины» (как каталог —
 * реестром `SCREEN_PANELS`).
 */

import { ArrowLeft, Lock, X } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "../../ui/button";
import { EmptyState } from "../../ui/EmptyState";
import { Skeleton } from "../../ui/skeleton";
import { HeaderFooterPanel } from "../../site-view/header-footer/HeaderFooterPanel";
import { useSiteView } from "../../site-view/site-view-context";
import { blockTitle } from "../../site-pages/block-meta";
import { useBuilder } from "../builder-context";
import { BlockPanel } from "./BlockPanel";
import { PageSettingsPanel } from "./PageSettingsPanel";
import { WholeSitePanel } from "./WholeSitePanel";
import { PAGES_FORBIDDEN_TEXT } from "../../../hooks/site-builder/useBuilderPages";
import { BUILDER_PANEL_META, BUILDER_SCREEN_TITLES, isOwnScreenPanel, SCREEN_PANELS, productTemplateScope, selectionScope } from "../../../lib/site-builder/panels";
import { PREVIEW_SCREENS } from "../../../lib/site-view/preview";
import { cn } from "@/lib/utils";

/** Пояснение справа на экране магазина (реестр: свой текст — запись). */
const SCREEN_PANEL_DEFAULT =
  "Список товаров, фильтры и шаги заказа на этом экране задаёт платформа. Здесь меняется только вид: шапка, подвал и оформление — в разделах «Весь сайт».";
const SCREEN_PANEL_TEXT: Readonly<Partial<Record<string, string>>> = {
  blog: "Лента статей и страница статьи — вид в панели «Блог». Блоки над и под лентой добавляются как на обычной странице: слева в структуре.",
};

/** Шаблон страницы товара без выбранного блока (uiux-design-r2a §3). */
const TEMPLATE_PANEL_TEXT =
  "Шаблон страницы товара — одни блоки для всех товаров. Выберите блок или элемент слева, чтобы изменить. Готовые раскладки — в «Добавить блок → Страница товара». Правки попадут на сайт после «Опубликовать».";

interface Props {
  /** Раскрыта на место канвы (широкий раздел на компьютере). */
  spread?: boolean;
  /** Закрыть — на телефоне и планшете панель поверх списка. */
  onClose?: () => void;
  /**
   * Назад к списку (планшет, телефон на весь экран): одна «←» перед
   * названием вместо [×] — у панели один способ уйти.
   */
  onBack?: () => void;
  /** Справа в шапке панели (например, «Смотреть на сайте»). */
  headerExtra?: ReactNode;
  className?: string;
}

export function BuilderPanel({ spread = false, onClose, onBack, headerExtra, className }: Props) {
  const b = useBuilder();
  const view = useSiteView();
  const { selection, pages } = b;
  const pageCount = pages.list?.length ?? 0;
  const page = pages.page;
  const panel = selection.panel && selection.panel !== "pages" ? selection.panel : null;
  const block = selection.sel.kind === "block" ? (page?.blocks.find((x) => x.id === (selection.sel as { id: string }).id) ?? null) : null;
  const screen = selection.screen ? PREVIEW_SCREENS.find((s) => s.key === selection.screen) : null;
  const template = selection.screen === "product";
  // Экран со своим видом (каталог, корзина) без явного раздела — его панель (реестр SCREEN_PANELS).
  const screenPanel = !panel && selection.sel.kind !== "area" && screen && selection.screen ? (SCREEN_PANELS[selection.screen] ?? null) : null;
  const shown = panel ?? screenPanel;

  let title = "Настройки страницы";
  if (shown) title = BUILDER_PANEL_META[shown].label;
  else if (selection.sel.kind === "area") title = "Шапка и подвал";
  else if (block) title = blockTitle(block);
  else if (screen) title = BUILDER_SCREEN_TITLES[screen.key] ?? screen.label;
  // Шапка и подвал — весь сайт, даже если выбраны на экране магазина.
  const scope = shown
    ? BUILDER_PANEL_META[shown].scope(pageCount)
    : selection.sel.kind === "area" || !screen
      ? selectionScope(selection.sel, page?.title ?? null, pageCount)
      : template
        ? productTemplateScope(pages.template?.appliesTo ?? null)
        : "Экран магазина — меняется только вид";
  const local = !panel && selection.sel.kind !== "area" && !screen;
  // Раздел экрана со своим видом (каталог, корзина) закрывать некуда: без него справа то же самое — ✕ не показываем.
  const ownScreenPanel = isOwnScreenPanel(selection.screen, panel);
  const closable = (panel !== null && !ownScreenPanel) || selection.sel.kind !== "page";

  function close() {
    if (onClose) onClose();
    else void b.select(panel ? { panel: null } : { sel: { kind: "page" } });
  }

  function body() {
    if (!view.draft) return <Skeleton className="h-64 rounded-xl" aria-label="Загрузка" />;
    if (panel) return <WholeSitePanel panel={panel} draft={view.draft} spread={spread} />;
    if (selection.sel.kind === "area") {
      if (!view.editableParts.includes("THEME")) {
        return <EmptyState variant="forbidden" icon={Lock} title="Только просмотр" description={BUILDER_PANEL_META.appearance.forbidden} />;
      }
      const area = selection.sel.area;
      return <HeaderFooterPanel tab={area} onTab={(next) => void b.select({ sel: { kind: "area", area: next } })} />;
    }
    if (screenPanel) return <WholeSitePanel panel={screenPanel} draft={view.draft} spread={spread} />;
    if (screen && !template) return <p className="type-body text-on-surface-variant">{SCREEN_PANEL_TEXT[screen.key] ?? SCREEN_PANEL_DEFAULT}</p>;
    if (!b.canEditPages) return <p className="type-body text-on-surface-variant">{PAGES_FORBIDDEN_TEXT}</p>;
    if (pages.pageError) return <p className="type-body text-on-surface-variant">{pages.pageError}</p>;
    if (!page) return <Skeleton className="h-64 rounded-xl" aria-label="Загрузка страницы" />;
    if (selection.sel.kind === "block") {
      return block ? <BlockPanel key={block.id} block={block} /> : <p className="type-body text-on-surface-variant">Блок не найден — возможно, его удалили.</p>;
    }
    if (page.kind === "TEMPLATE") return <p className="type-body text-on-surface-variant">{TEMPLATE_PANEL_TEXT}</p>;
    return <PageSettingsPanel page={page} />;
  }

  return (
    <section aria-label={title} className={cn("@container flex min-h-0 min-w-0 flex-col", className)}>
      <div className="flex shrink-0 items-center gap-2 pb-2">
        {onBack ? (
          <Button variant="ghost" size="icon" onClick={onBack} aria-label="Назад к структуре">
            <ArrowLeft aria-hidden />
          </Button>
        ) : null}
        {/* Длинное имя блока («Сведения о товаре и продавец») на 360 — в две строки, не обрезкой (QA Р2а). */}
        <h2 className="type-section-title line-clamp-2 min-w-0 flex-1 break-words text-on-surface" title={title}>
          {title}
        </h2>
        {headerExtra}
        {!onBack && (closable || onClose) ? (
          <Button variant="ghost" size="icon" onClick={close} aria-label="Закрыть панель">
            <X aria-hidden />
          </Button>
        ) : null}
      </div>
      <p
        className={cn(
          // На телефоне — плотнее, но текст переносится (на 360 обрезка «…магаз…» прятала смысл, QA SB122).
          "type-caption mb-3 shrink-0 break-words rounded-lg border-l-4 px-3 py-2 max-sm:mb-2 max-sm:py-1",
          local ? "border-on-surface-variant/40 bg-surface-low text-on-surface-variant" : "border-primary bg-primary/8 text-on-surface",
        )}
      >
        {scope}
      </p>
      {/* У панели блока внизу липкая «Сохранить блок»: нижний отступ прокрутки
          поднял бы её на 24 px над краем (липкость считается от края минус отступ). */}
      <div className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain", !block && "pb-6 max-sm:pb-0")}>{body()}</div>
    </section>
  );
}
